import { useState, useEffect } from 'react';
import { useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import {
  User,
  subscribeToAuthState,
  signInWithGoogle,
  signOut,
  redirectSignInError,
} from '../utils/firebase';
import api from '../services/api';
import { clearAllDraftEntries } from '../utils/draftEntries';
import { setUser as setReduxUser, logout as reduxLogout } from '../store/authSlice';

interface AuthState {
  user: User | null;
  loading: boolean;
  error: Error | null;
}

/**
 * ONE /auth/me per signed-in user, shared by every useAuth() instance (QA
 * Q41: ~10 components each fetched it on mount). The first instance to see a
 * uid starts the request and is the only one that dispatches its result —
 * later instances must NOT re-dispatch a cached (possibly older) profile over
 * Redux, which other flows (onboarding, Settings) update directly. A failed
 * request is dropped from the cache so the NEXT mount may retry; nothing
 * retries in a loop. Cleared on sign-out so a new sign-in refetches.
 */
let meRequest: { uid: string; promise: Promise<any> } | null = null;

function fetchMeOnce(uid: string): { promise: Promise<any>; isNew: boolean } {
  if (meRequest && meRequest.uid === uid) return { promise: meRequest.promise, isNew: false };
  const promise = api.get('/auth/me').then((r) => r.data);
  meRequest = { uid, promise };
  promise.catch(() => {
    if (meRequest?.promise === promise) meRequest = null;
  });
  return { promise, isNew: true };
}

/** Sign-out / tests: forget the shared /auth/me result. */
export function resetAuthMeCache(): void {
  meRequest = null;
}

/** Closing or replacing the Google popup is the user's choice, not a failure. */
const CANCELLED_SIGN_IN = new Set([
  'auth/popup-closed-by-user',
  'auth/cancelled-popup-request',
  'auth/redirect-cancelled-by-user',
  'auth/user-cancelled',
]);

/**
 * Copy for the sign-in banner, or null when there is nothing to report.
 * Never shows Firebase's own text ("Firebase: Error (auth/...)").
 */
export function signInErrorMessage(error: unknown): string | null {
  const code = (error as { code?: string } | null)?.code ?? '';
  if (CANCELLED_SIGN_IN.has(code)) return null;
  if (code === 'auth/popup-blocked') {
    return 'Your browser blocked the Google sign-in window. Allow pop-ups for iadvocate.ai and try again.';
  }
  if (code === 'auth/network-request-failed') {
    return 'Could not reach Google sign-in. Check your connection and try again.';
  }
  return 'Google sign-in did not complete. Please try again.';
}

export const useAuth = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [state, setState] = useState<AuthState>({
    user: null,
    loading: true,
    error: null,
  });

  useEffect(() => {
    let active = true;
    // Installed app: a redirect sign-in that failed reports here, after the
    // page reloads — there is no awaiting login() call left to catch it.
    redirectSignInError().then((error) => {
      const message = error ? signInErrorMessage(error) : null;
      if (message && active) setState((prev) => ({ ...prev, error: new Error(message) }));
    });

    const unsubscribe = subscribeToAuthState(async (firebaseUser) => {
      // Keep a redirect error visible while signed out; a sign-in clears it.
      setState((prev) => ({
        user: firebaseUser,
        loading: false,
        error: firebaseUser ? null : prev.error,
      }));

      // Hydrate Redux auth.user from backend /auth/me. Without this,
      // state.auth.user stays null for already-onboarded users and any
      // feature that reads practice_area from Redux silently no-ops.
      if (firebaseUser) {
        const { promise, isNew } = fetchMeOnce(firebaseUser.uid);
        if (!isNew) return; // another instance owns this fetch + dispatch
        try {
          const data = await promise;
          dispatch(
            setReduxUser({
              id: data.id,
              email: data.email,
              displayName: data.display_name ?? firebaseUser.displayName ?? undefined,
              subscriptionTier: data.subscription_tier ?? 'free',
              primary_practice_area: data.primary_practice_area ?? null,
              practice_areas: data.practice_areas ?? [],
              // Default to true on missing/undefined so a transient API blip
              // doesn't unexpectedly trap an already-onboarded user back into
              // the wizard. The wizard fires only when this is explicitly false.
              is_onboarded: data.is_onboarded ?? true,
              // Defaults to 'none' (not 'granted') so a stale/blank response
              // never implies access the backend hasn't confirmed.
              corporate_access: data.corporate_access ?? 'none',
              // Same fail-closed default: a blank response never implies staff access.
              staff_role: data.staff_role === 'support' ? 'support' : 'none',
              terms_accepted_at: data.terms_accepted_at ?? null,
              terms_version: data.terms_version ?? null,
            }),
          );
        } catch {
          // Non-fatal: if /auth/me fails (network, token not yet minted),
          // the UI just falls back to "no practice area" defaults. The
          // Firebase user object still lets the app render.
        }
      } else {
        resetAuthMeCache();
        // In-progress drafts hold client details: never outlive the session.
        clearAllDraftEntries();
        dispatch(reduxLogout());
      }
    });

    return () => {
      active = false;
      unsubscribe();
    };
  }, [dispatch]);

  // `loading` is NOT set here. Firebase only notices a closed popup by
  // polling, then waits ~8s more before rejecting — holding `loading` that
  // long left every sign-in button disabled after the user simply cancelled
  // the account picker. A second click while a popup is pending is safe:
  // Firebase cancels the first (cancelled-popup-request) and opens a new one.
  const login = async () => {
    setState((prev) => ({ ...prev, error: null }));
    try {
      await signInWithGoogle();
    } catch (error) {
      const message = signInErrorMessage(error);
      if (message) setState((prev) => ({ ...prev, error: new Error(message) }));
    }
  };

  const logout = async () => {
    try {
      setState((prev) => ({ ...prev, loading: true }));
      await signOut();
      navigate('/', { replace: true });
    } catch (error) {
      setState((prev) => ({
        ...prev,
        loading: false,
        error: error as Error,
      }));
    }
  };

  return {
    user: state.user,
    loading: state.loading,
    error: state.error,
    login,
    logout,
  };
};

export default useAuth;
